import type { ErrorCode } from '@quiz/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'INTERNAL' | 'VALIDATION_FAILED',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  token?: string | null;
  /** Abort after this long. Defaults to REQUEST_TIMEOUT_MS. */
  timeoutMs?: number;
}

/**
 * Bad pub wifi does not fail requests, it hangs them. Without a deadline a
 * single stalled poll would sit in flight forever and the phone would quietly
 * stop updating for the rest of the night -- looking, to the player, exactly
 * like the app being broken.
 */
export const REQUEST_TIMEOUT_MS = 8000;

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: {
      ...(opts.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(opts.token ? { authorization: `Bearer ${opts.token}` } : {}),
    },
    ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    signal: AbortSignal.timeout(opts.timeoutMs ?? REQUEST_TIMEOUT_MS),
  });

  if (res.status === 204) return null as T;

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = body?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Something went wrong.',
      err?.details,
    );
  }
  return body as T;
}

/** 204 means "nothing changed", which is a normal answer, not an absence. */
export async function apiMaybe<T>(path: string, opts: Options = {}): Promise<T | null> {
  const res = await fetch(path, {
    headers: opts.token ? { authorization: `Bearer ${opts.token}` } : {},
    signal: AbortSignal.timeout(opts.timeoutMs ?? REQUEST_TIMEOUT_MS),
  });
  if (res.status === 204) return null;
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, body?.error?.code ?? 'INTERNAL', body?.error?.message ?? 'Error');
  }
  return body as T;
}
